import React, { useEffect, useMemo } from 'react'
import {
  TooltipPositions,
  TooltipOverlayType,
  Tooltip,
} from 'src/components/Reusable/Tooltip/Tooltip'
import { AuditRecord } from '@trustero/trustero-api-web/lib/audit/audit_pb'
import {
  Control,
  CONTROL_STATUS,
} from '@trustero/trustero-api-web/lib/model/control_pb'
import { AUDIT_RESULT } from '@trustero/trustero-api-web/lib/audit/auditbot_pb'
import { NoPaddingGridColumn } from 'src/components/Reusable/Grid/Grid.styles'
import { TitleText } from 'src/components/Reusable/Text/Title'
import { KeyedMutator } from 'swr'
import {
  ControlPolicyInfo,
  PolicyInfo,
} from 'src/components/Reusable/ListItem/ListItem.components'
import { useGetControlChecks } from 'src/pages/GRCDashboards/Compliance/ControlChecksDashboardWidget/ControlChecksWidget.hooks'
import { CONTROL_TEST_NAMES } from 'src/pages/AuditBot/accordion/subsection/ControlChecks/AuditBotControlChecks.constants'
import { useAuthorizedGrpcClient } from 'src/adapter'
import { ModelPromiseClient } from '@trustero/trustero-api-web/lib/model/model_grpc_web_pb'
import {
  Identifier,
  MODEL_TYPE,
} from '@trustero/trustero-api-web/lib/common/model_pb'
import { ControlStatusRequest } from '@trustero/trustero-api-web/lib/model/model_pb'
import isFunction from 'lodash/isFunction'
import { LIST_ITEM_COPY } from 'src/components/Reusable/ListItem/ListItem.constants'
import { Pill } from 'src/components/Reusable/Pill'
import palette from 'src/designSystem/variables/palette'
import { ControlStatusDropdown } from 'src/components/PageLayout/ShowPage/Dropdowns/ControlStatusDropdown'
import { useAuditScanProgress } from 'src/pages/Roadmap_v2/smartchecks/context/AuditScanProgressContext'
import { FlexAlign, FlexRow } from 'src/components/Reusable/Flex'
import { Spinner } from 'src/Throbber'
import { ContentMatchBadge } from 'src/components/Reusable/Search/ContentSearch/ContentSearch.components'
import { ActionGridColumn, GridColumn } from '../../../components/Reusable/Grid'
import { DepartmentChip } from '../DepartmentChip'
import { BasicChip } from '../../../components/Reusable/Chips/BasicChip/BasicChip'
import { ModelComplianceFrameWorkInfo } from '../../../components/Reusable/Text/ModelComplianceFrameworkInfo'
import { useComplianceFrameworks } from '../../../components/async/complianceframework/useComplianceFrameworks'
import { MODEL_TYPES } from '../../../Utils/globalEnums'
import { StyledModelId } from '../ControlsShowPage/ControlsShowPage.styles'
import { OwnerAssignmentButton } from './OwnerAssignmentButton'
import { DescriptionMarkup } from './DescriptionMarkup'
import { ControlsListItemMenu } from './ControlsListItemMenu/ControlsListItemMenu'
import {
  ControlCheckResultPillsContainer,
  PolicyGridColumn,
} from './ControlsListItem.styles'
import {
  getComplianceFrameworksForControl,
  getControlCheckResultPillColor,
  getControlCheckResultPillLabel,
  getControlsListItemConfig,
  getMismatch,
} from './ControlsListItem.helpers'
import { ControlListItemConfig } from './ControlListItem.constants'

const MAX_MODEL_ID_LENGTH = 20

export const ControlModelId = ({
  modelId,
}: {
  modelId: string
}): JSX.Element => {
  return modelId.length > MAX_MODEL_ID_LENGTH ? (
    <Tooltip
      id={`controls-index-row-modelid-tooltip-${modelId}`}
      placement={TooltipPositions.top}
      overlayType={TooltipOverlayType.popover}
      tooltipBody={modelId}
    >
      <StyledModelId>{modelId}</StyledModelId>
    </Tooltip>
  ) : (
    <>{modelId}</>
  )
}

export const ControlChecksListItemNoTestResult = ({
  isNotApplicable,
  testName,
}: {
  isNotApplicable: boolean
  testName: CONTROL_TEST_NAMES
}): JSX.Element => {
  return isNotApplicable ? (
    <Pill
      label={getControlCheckResultPillLabel(AUDIT_RESULT.NOT_APPLICABLE)}
      backgroundColor={getControlCheckResultPillColor(
        AUDIT_RESULT.NOT_APPLICABLE,
      )}
      $fontColor={palette.neutral.white}
      $size="small"
    />
  ) : (
    <Pill
      label={getControlCheckResultPillLabel(AUDIT_RESULT.NOT_RUN, testName)}
      backgroundColor={getControlCheckResultPillColor(AUDIT_RESULT.NOT_RUN)}
      $fontColor={palette.neutral.white}
      $size="small"
    />
  )
}

export const ControlCheckResultPills = ({
  result,
  testName,
  isStale,
}: {
  result: AUDIT_RESULT
  testName: CONTROL_TEST_NAMES
  isStale: boolean
}): JSX.Element => {
  return (
    <ControlCheckResultPillsContainer>
      <Pill
        label={getControlCheckResultPillLabel(result, testName)}
        backgroundColor={getControlCheckResultPillColor(result)}
        $fontColor={palette.neutral.white}
        $size="small"
      />
      {isStale && (
        <Pill
          label="Outdated"
          backgroundColor={palette.orange['700']}
          $fontColor={palette.neutral.white}
          $size="small"
        />
      )}
    </ControlCheckResultPillsContainer>
  )
}

export const ControlsListItemMenuColumn = ({
  control,
  config,
}: {
  control: Control.AsObject
  config?: ControlListItemConfig
}): JSX.Element => {
  const menuConfig = getControlsListItemConfig(config)
  return (
    <ActionGridColumn>
      {menuConfig?.customMenu ? (
        <menuConfig.customMenu control={control} />
      ) : (
        <ControlsListItemMenu control={control} />
      )}
    </ActionGridColumn>
  )
}

export const ControlsListItemAssigneeColumn = ({
  control,
  mutate,
}: {
  control: Control.AsObject
  mutate: KeyedMutator<Control>
}): JSX.Element => (
  <GridColumn>
    <OwnerAssignmentButton control={control} mutate={mutate} />
  </GridColumn>
)

export const ControlsListItemStatusColumn = ({
  control,
  mutate,
}: {
  control: Control.AsObject
  mutate: KeyedMutator<Control>
}): JSX.Element => {
  const modelClient = useAuthorizedGrpcClient(ModelPromiseClient)

  const updateStatus = async (
    status: CONTROL_STATUS,
    customStatusId?: string,
  ) => {
    const identifierMsg = new Identifier()
      .setModelid(control.modelId)
      .setModeltype(MODEL_TYPE.CONTROL)
    const controlStatusMsg = new ControlStatusRequest()
      .setId(identifierMsg)
      .setStatus(status)
      .setCustomStatusId(customStatusId || '')
    await modelClient.setControlStatus(controlStatusMsg)
    if (isFunction(mutate)) {
      await mutate()
    }
  }

  return (
    <GridColumn>
      <ControlStatusDropdown
        control={control}
        initialStatus={control.status as CONTROL_STATUS}
        onSelect={updateStatus}
      />
    </GridColumn>
  )
}

export const ControlsListItemEvidenceColumn = ({
  show,
  control,
}: {
  show: boolean
  control: Control.AsObject
}): JSX.Element => {
  return <GridColumn>{show && <>{control.linkedEvidenceCount}</>}</GridColumn>
}

export const ControlsListItemDepartmentColumn = ({
  show,
  departmentId,
  departmentNames,
}: {
  show: boolean
  departmentId: string
  departmentNames?: string[]
}): JSX.Element => (
  <GridColumn>
    {show &&
      (departmentNames && departmentNames.length > 0 ? (
        <DepartmentSummaryBadge departmentNames={departmentNames} />
      ) : (
        <DepartmentChip departmentId={departmentId} />
      ))}
  </GridColumn>
)

const DepartmentSummaryBadge = ({
  departmentNames,
}: {
  departmentNames: string[]
}): JSX.Element => {
  const uniqueDepartmentNames = useMemo(
    () => Array.from(new Set(departmentNames.filter(Boolean))),
    [departmentNames],
  )

  if (uniqueDepartmentNames.length === 0) {
    return <BasicChip>-</BasicChip>
  }

  const [primaryDepartment, ...remainingDepartments] = uniqueDepartmentNames
  const hasMore = remainingDepartments.length > 0

  return hasMore ? (
    <Tooltip
      id={`controls-index-departments-tooltip-${uniqueDepartmentNames.join(
        '-',
      )}`}
      placement={TooltipPositions.top}
      overlayType={TooltipOverlayType.popover}
      tooltipBody={remainingDepartments.join(', ')}
    >
      <div>
        <BasicChip>{`${primaryDepartment} + ${remainingDepartments.length} more`}</BasicChip>
      </div>
    </Tooltip>
  ) : (
    <BasicChip>{primaryDepartment}</BasicChip>
  )
}

export const ControlsListItemContentMatchColumn = ({
  isContentMatch,
}: {
  isContentMatch: boolean
}): JSX.Element => (
  <GridColumn>
    <ContentMatchBadge isContentMatch={isContentMatch} />
  </GridColumn>
)

export const ControlsListItemModelIdColumn = ({
  modelId,
}: {
  modelId: string
}): JSX.Element => (
  <GridColumn>
    <ControlModelId modelId={modelId} />
  </GridColumn>
)

export const ControlsListItemControlNameColumn = ({
  control,
  isControlNa,
  policyNames,
}: {
  control: Control.AsObject
  isControlNa: boolean
  policyNames?: string[]
}): JSX.Element => (
  <PolicyGridColumn>
    <Tooltip
      id={`controls-index-row-tooltip-${control.name}`}
      placement={TooltipPositions.right}
      overlayType={TooltipOverlayType.popover}
      tooltipBody={<DescriptionMarkup objective={control.objective} />}
    >
      <TitleText>{control.name}</TitleText>
    </Tooltip>
    {policyNames && policyNames.length > 1 ? (
      <Tooltip
        id={`controls-index-row-policies-tooltip-${control.id}`}
        placement={TooltipPositions.top}
        overlayType={TooltipOverlayType.popover}
        tooltipBody={policyNames.join(', ')}
      >
        <div style={{ width: '100%', minWidth: 0 }}>
          <PolicyInfo policyName={policyNames.join(', ')} isWarning={false} />
        </div>
      </Tooltip>
    ) : (
      <ControlPolicyInfo
        control={{
          ...control,
          policyName:
            policyNames && policyNames.length > 0
              ? policyNames.join(', ')
              : control.policyName,
        }}
        isControlNa={isControlNa}
      />
    )}
  </PolicyGridColumn>
)

export const ControlsListItemFrameworksColumn = ({
  audit,
  complianceFrameworkIds,
}: {
  audit?: AuditRecord
  complianceFrameworkIds: string[]
}): JSX.Element => {
  const { data: cfData, isLoading: isCfLoading } = useComplianceFrameworks(true)

  if (!cfData || isCfLoading) {
    return (
      <GridColumn>
        <></>
      </GridColumn>
    )
  }

  const complianceFrameworks = cfData.getItemsList() || []

  const complianceFrameworksForControl = getComplianceFrameworksForControl(
    complianceFrameworks,
    complianceFrameworkIds,
  )
  const complianceFrameworksInUse = audit
    ? audit.getComplianceFrameworksList()
    : complianceFrameworks
  const mismatch = getMismatch(
    complianceFrameworksForControl.map((el) => el.getModelId()),
    complianceFrameworksInUse,
    !!audit,
  )
  const frameworkNames = complianceFrameworksForControl
    .map((framework) => framework.getName())
    .join(', ')
  const hasComplianceFrameworks = complianceFrameworksForControl.length > 0
  const cfNames = hasComplianceFrameworks
    ? frameworkNames
    : LIST_ITEM_COPY.NO_FRAMEWORK

  return (
    <GridColumn>
      <ModelComplianceFrameWorkInfo
        modelComplianceFrameworkNames={cfNames}
        isWarning={!hasComplianceFrameworks}
        mismatch={mismatch}
        modelType={MODEL_TYPES.CONTROL}
      />
    </GridColumn>
  )
}

export const ControlsListItemAIControlCheckColumns = ({
  controlId,
  isNotApplicable,
  migratedControlId,
  showCPMD,
  showOE,
}: {
  controlId: string
  isNotApplicable: boolean
  migratedControlId?: string
  showCPMD: boolean
  showOE: boolean
}): JSX.Element => {
  // use the control id to get the test results
  const { tests, mutate, isLoading } = useGetControlChecks(
    controlId,
    true,
    migratedControlId,
  )
  const { getAuditRunId } = useAuditScanProgress()
  const isExamining = useMemo(
    () =>
      !!getAuditRunId(migratedControlId || controlId, [
        CONTROL_TEST_NAMES.POLICY_MATCH,
      ]),
    [migratedControlId, controlId, getAuditRunId],
  )
  const isTesting = useMemo(
    () =>
      !!getAuditRunId(migratedControlId || controlId, [
        CONTROL_TEST_NAMES.SPOT_CHECK,
        CONTROL_TEST_NAMES.COMPLETENESS,
      ]),
    [migratedControlId, controlId, getAuditRunId],
  )

  useEffect(() => {
    // mutate when the examining or testing state changes
    mutate()
  }, [isExamining, isTesting, mutate])

  if (isLoading) {
    return (
      <>
        {showCPMD && (
          <NoPaddingGridColumn>
            <span>-</span>
          </NoPaddingGridColumn>
        )}
        {showOE && (
          <NoPaddingGridColumn>
            <span>-</span>
          </NoPaddingGridColumn>
        )}
      </>
    )
  }

  if (!tests || isNotApplicable) {
    return (
      <>
        {showCPMD && (
          <NoPaddingGridColumn>
            <ControlCheckResultPillsContainer>
              <ControlChecksListItemNoTestResult
                isNotApplicable={isNotApplicable}
                testName={CONTROL_TEST_NAMES.POLICY_MATCH}
              />
            </ControlCheckResultPillsContainer>
          </NoPaddingGridColumn>
        )}
        {showOE && (
          <NoPaddingGridColumn>
            <ControlCheckResultPillsContainer>
              <ControlChecksListItemNoTestResult
                isNotApplicable={isNotApplicable}
                testName={CONTROL_TEST_NAMES.SPOT_CHECK}
              />
            </ControlCheckResultPillsContainer>
          </NoPaddingGridColumn>
        )}
      </>
    )
  }

  const cpmd = tests.find(
    (test) => test.getTestName() === CONTROL_TEST_NAMES.POLICY_MATCH,
  )

  const spotCheck = tests.find(
    (test) => test.getTestName() === CONTROL_TEST_NAMES.SPOT_CHECK,
  )

  return (
    <>
      {showCPMD && (
        <NoPaddingGridColumn>
          {isExamining ? (
            <FlexRow gap={8} align={FlexAlign.CENTER}>
              <Spinner color="primary" size="s" />
              <span>Examining</span>
            </FlexRow>
          ) : cpmd ? (
            <ControlCheckResultPills
              result={cpmd.getResult()}
              testName={CONTROL_TEST_NAMES.POLICY_MATCH}
              isStale={!!cpmd.getIsStale()}
            />
          ) : (
            <ControlCheckResultPillsContainer>
              <ControlChecksListItemNoTestResult
                isNotApplicable={isNotApplicable}
                testName={CONTROL_TEST_NAMES.POLICY_MATCH}
              />
            </ControlCheckResultPillsContainer>
          )}
        </NoPaddingGridColumn>
      )}
      {showOE && (
        <NoPaddingGridColumn>
          {isTesting ? (
            <FlexRow gap={8} align={FlexAlign.CENTER}>
              <Spinner color="primary" size="s" />
              <span>Testing</span>
            </FlexRow>
          ) : spotCheck ? (
            <ControlCheckResultPills
              result={spotCheck.getResult()}
              testName={CONTROL_TEST_NAMES.SPOT_CHECK}
              isStale={!!spotCheck.getIsStale()}
            />
          ) : (
            <ControlCheckResultPillsContainer>
              <ControlChecksListItemNoTestResult
                isNotApplicable={isNotApplicable}
                testName={CONTROL_TEST_NAMES.SPOT_CHECK}
              />
            </ControlCheckResultPillsContainer>
          )}
        </NoPaddingGridColumn>
      )}
    </>
  )
}
